import type { ReactNode } from "react";
import { ArrowDown, Check } from "lucide-react";

/**
 * Filling a document in by typing into it, the way SignEasy does.
 *
 * ## Why the blanks are in the document and not in a form under it
 *
 * The form worked. What it could not do is tell somebody *which* blank they
 * were filling: a box labelled "National ID" sits under twenty-five clauses,
 * and the sentence it belongs to is somewhere above, out of view. People
 * filled it in and then scrolled back to check the right gap had moved, which
 * is the reading equivalent of counting on your fingers.
 *
 * Typing into the sentence removes the question. The gap is the field, the
 * sentence around it is the context, and there is nothing to check afterwards.
 *
 * ## What the highlight is for
 *
 * Every gap somebody still has to fill is marked, so a document opened on a
 * phone can be skimmed for what is outstanding rather than read twice. A
 * filled one keeps a quiet underline — it is still a thing they typed, and it
 * should still look different from the printed text around it — and the
 * printed copy has neither, because ink on paper is not a form.
 */

export type InlineFieldSpec = {
  key: string;
  label: string;
  kind: string;
  required: boolean;
  hint?: string;
  options?: ReadonlyArray<{ value: string; label: string }>;
};

/**
 * The attribute that marks a gap, so anything can find it again.
 *
 * Exported because the signature box on each signing page is a gap too — it is
 * the last one to fill and the only one that is not in the document — and it
 * has to carry the same mark for "Next blank" to be able to walk somebody all
 * the way to the pen.
 */
export const GAP_ATTRIBUTE = "data-gap";

/** One gap in the text: an input sized to what it holds. */
export function InlineField({
  field,
  value,
  onChange,
}: {
  field: InlineFieldSpec;
  value: string;
  onChange: (value: string) => void;
}) {
  const filled = value.trim().length > 0;
  const isSignature = field.kind === "signature" || field.kind === "initials";

  /*
   * Width follows the content, with a floor.
   *
   * A blank the width of the whole column looks like a form field dropped into
   * a paragraph; one that grows with what is typed reads as writing on a line.
   */
  const width = Math.max(field.label.length, value.length + 2, 12);

  if (field.kind === "choice" && field.options) {
    return (
      <select
        {...{ [GAP_ATTRIBUTE]: field.key }}
        className={`inline-gap ${filled ? "is-filled" : ""}`}
        value={value || field.options[0]?.value || ""}
        onChange={(event) => onChange(event.target.value)}
        aria-label={field.label}
        title={field.hint ?? field.label}
      >
        {field.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      {...{ [GAP_ATTRIBUTE]: field.key }}
      className={`inline-gap ${filled ? "is-filled" : ""} ${isSignature ? "is-signature" : ""}`}
      type={
        field.kind === "date"
          ? "date"
          : field.kind === "money"
            ? "number"
            : "text"
      }
      inputMode={field.kind === "money" ? "numeric" : undefined}
      style={{ width: `${width}ch` }}
      value={value}
      placeholder={field.label}
      onChange={(event) => onChange(event.target.value)}
      aria-label={field.label}
      title={field.hint ?? field.label}
    />
  );
}

/**
 * What is left to do, and the way to finish — pinned to the bottom.
 *
 * Twenty-five clauses is a long way to scroll, and somebody who has filled in
 * four of five gaps should not have to hunt for the fifth or wonder whether
 * the button at the end will refuse them. The count is live, "Next blank"
 * jumps to the first one outstanding, and signing is right there once nothing
 * is outstanding.
 */
export function SigningBar({
  outstanding,
  total,
  onJump,
  children,
}: {
  outstanding: InlineFieldSpec[];
  total: number;
  onJump: (key: string) => void;
  /** The signature control itself — different on each side of a document. */
  children: ReactNode;
}) {
  const done = total - outstanding.length;
  /*
   * The first gap still outstanding, or nothing.
   *
   * Named rather than indexed twice: `outstanding[0]` under `noUncheckedIndexedAccess`
   * is `T | undefined` at every use, and a bar that reads it in two places
   * would need the same guard twice and get it wrong once.
   */
  const next = outstanding[0];

  return (
    <>
      {/*
        The space the bar would have occupied.
        ---------------------------------------
        Rendered here rather than left to each page, because a bar that is
        fixed to the window covers whatever is at the end of the document, and
        a caller who forgot the spacer would ship a page whose last control
        cannot be reached. Three screens use this; one of them forgetting is a
        question of when.
      */}
      <div className="signing-bar-spacer print:hidden" aria-hidden="true" />

      <div className="signing-bar print:hidden">
        <div className="signing-bar-inner">
          <div className="min-w-0">
            {outstanding.length === 0 ? (
              <p className="flex items-center gap-1.5 text-sm font-medium text-secondary-800">
                <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
                Everything is filled in.{" "}
                {total > 0 ? `All ${total} of them.` : ""}
              </p>
            ) : (
              <p className="truncate text-sm">
                <span className="font-medium">
                  {done} of {total} filled in
                </span>
                <span className="text-muted-foreground">
                  {" "}
                  · next: {next?.label}
                </span>
              </p>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {next && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => onJump(next.key)}
              >
                <ArrowDown className="h-4 w-4" aria-hidden="true" />
                Next blank
              </button>
            )}
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * The gaps, the values, and a way to jump to one.
 *
 * Kept here so every signing screen gets the same behaviour from the same
 * place: the customer's, the landlord's and the employee's sides of a document
 * should not drift apart in how they feel to fill in.
 *
 * ## Why this is not a hook
 *
 * Because it cannot be one. Every page that needs it returns early while the
 * document is loading, and the document is what the gaps are computed from —
 * so the call site is necessarily below an early return, which is exactly
 * where a hook may not go.
 *
 * It does not need to be one either. The only state a hook version held was a
 * map from key to input element, and that map is already in the page: each
 * input renders with its key on it. Reading it back with a selector is the
 * shorter version of the same thing, with no registration to keep in step and
 * nothing to clean up.
 */
export function inlineFilling(
  fields: InlineFieldSpec[],
  values: Record<string, unknown>,
  draft: Record<string, unknown>,
) {
  const valueOf = (key: string): string => {
    const chosen = draft[key] ?? values[key];
    return chosen === undefined || chosen === null ? "" : String(chosen);
  };

  const outstanding = fields.filter(
    (field) => field.required && valueOf(field.key).trim().length === 0,
  );

  const jumpTo = (key: string) => {
    /*
     * Keys are `[A-Za-z0-9_]` — the same alphabet `fillLetter` accepts in a
     * placeholder — so they need no escaping in a selector, and one that did
     * would not be a placeholder in any document.
     */
    const node = document.querySelector<HTMLElement>(
      `[${GAP_ATTRIBUTE}="${key}"]`,
    );
    if (!node) return;

    node.scrollIntoView({ behavior: "smooth", block: "center" });
    // Focus after the scroll settles, or the browser jumps twice.
    window.setTimeout(() => node.focus({ preventScroll: true }), 320);
  };

  return { valueOf, outstanding, jumpTo };
}
